/*
eWorldES Proprietary Software
Copyright (C) 2025 eWorldES. All rights reserved.

This software is proprietary to eWorldES and is protected by copyright laws and international treaties.
You may not use, copy, modify, distribute, or disclose this software without explicit written permission from eWorldES.
Any unauthorized use or reproduction of this software is strictly prohibited and may result in legal action.

License Agreement:
By using this software, you agree to be bound by the terms of the eWorldES Proprietary Software License Agreement.
For a copy of the agreement, visit https://eworldes.com/ or contact eWorldES directly for more information.

This software is provided "AS IS" without warranty of any kind, express or implied, including but not limited to the warranties of
merchantability, fitness for a particular purpose, or non-infringement. In no event shall eWorldES be liable for any damages
arising out of the use of this software, even if eWorldES has been advised of the possibility of such damages.

For inquiries or requests regarding this software, please contact eWorldES at:
Email: info@eworldes.com
Website: https://eworldes.com/
*/

import { useTheme } from "@mui/material";
import type {} from "@mui/material/themeCssVarsAugmentation";
import useMediaQuery from "@mui/material/useMediaQuery";
import * as React from "react";
import { Box } from "../Box";
import { Toolbar } from "../Toolbar";
import {
  NavigationContext,
  WindowContext,
} from "../../context/Context";
import Footer from "../Footer/Footer";
import { Header } from "../Header";
import { SidebarNavigationDrawer } from "../SidebarNavigation";
import { AppLayoutProps } from "./ILayout";

export interface SidebarFooterProps {
  mini: boolean;
}

const AppLayout = React.forwardRef<Element, AppLayoutProps>((props, ref) => {
  const {
    children,
    disableCollapsibleSidebar = false,
    defaultSidebarCollapsed = false,
    hideNavigation = false,
    sidebarExpandedWidth = 320,
    footerView,
    headerTheme,
    footerTheme,
    toolbarView,
    sidebarFooter,
    navExpandedToggleHandler,
  } = props;

  const theme = useTheme();
  const navigationContext = React.useContext(NavigationContext);
  const appWindowContext = React.useContext(WindowContext);
  const navigation = navigationContext;

  const [isDesktopNavigationExpanded, setIsDesktopNavigationExpanded] =
    React.useState(!defaultSidebarCollapsed);
  const [isMobileNavigationExpanded, setIsMobileNavigationExpanded] =
    React.useState(false);

  const FooterView = footerView ?? null;

  const isUnderMdViewport = useMediaQuery(
    theme.breakpoints.down("md"),
    appWindowContext && { matchMedia: appWindowContext.matchMedia },
  );

  const isOverSmViewport = useMediaQuery(
    theme.breakpoints.up("sm"),
    appWindowContext && { matchMedia: appWindowContext.matchMedia },
  );

  const isNavigationExpanded = isUnderMdViewport
    ? isMobileNavigationExpanded
    : isDesktopNavigationExpanded;

  const setIsNavigationExpanded = React.useCallback(
    (newExpanded: boolean) => {
      if (isUnderMdViewport) {
        setIsMobileNavigationExpanded(newExpanded);
      } else {
        setIsDesktopNavigationExpanded(newExpanded);
      }
    },
    [isUnderMdViewport],
  );

  const [isNavigationFullyExpanded, setIsNavigationFullyExpanded] =
    React.useState(isNavigationExpanded);

  React.useEffect(() => {
    if (isNavigationExpanded) {
      const drawerWidthTransitionTimeout = setTimeout(() => {
        setIsNavigationFullyExpanded(true);
      }, theme.transitions.duration.enteringScreen);
      return () => clearTimeout(drawerWidthTransitionTimeout);
    }
    setIsNavigationFullyExpanded(false);
    return () => {};
  }, [isNavigationExpanded, theme]);

  const selectedItemIdRef = React.useRef("");

  const handleSetNavigationExpanded = React.useCallback(
    (newExpanded: boolean) => () => {
      setIsNavigationExpanded(newExpanded);
    },
    [setIsNavigationExpanded],
  );

  const toggleNavigationExpanded = React.useCallback(() => {
    navExpandedToggleHandler && navExpandedToggleHandler();
    setIsNavigationExpanded(!isNavigationExpanded);
  }, [isNavigationExpanded, navExpandedToggleHandler, setIsNavigationExpanded]);

  const handleNavigationLinkClick = React.useCallback(() => {
    selectedItemIdRef.current = "";
    setIsMobileNavigationExpanded(false);
  }, []);

  React.useMemo(() => {
    if (navigation) {
      selectedItemIdRef.current = "";
    }
  }, [navigation]);

  const hasDrawerTransitions =
    isOverSmViewport && (disableCollapsibleSidebar || !isUnderMdViewport);

  const layoutRef = { current: ref as any };

  return (
    <Box
      ref={ref}
      sx={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        overflow: "hidden"
      }}
    >
      {/* Fixed Header */}
      <Header
        hideNavigation={hideNavigation}
        headerTheme={headerTheme}
        toolbarView={toolbarView}
        disableCollapsibleSidebar={disableCollapsibleSidebar}
        defaultSidebarCollapsed={defaultSidebarCollapsed}
        toggleNavigationExpanded={toggleNavigationExpanded}
        isDesktopNavigationExpanded={isDesktopNavigationExpanded}
        isMobileNavigationExpanded={isMobileNavigationExpanded}
      />

      <Box sx={{ display: "flex", flex: 1, minHeight: 0 }}>
        {!hideNavigation && (
          <SidebarNavigationDrawer
            sidebarExpandedWidth={sidebarExpandedWidth}
            disableCollapsibleSidebar={disableCollapsibleSidebar}
            isDesktopNavigationExpanded={isDesktopNavigationExpanded}
            isMobileNavigationExpanded={isMobileNavigationExpanded}
            isNavigationExpanded={isNavigationExpanded}
            hasDrawerTransitions={hasDrawerTransitions}
            isNavigationFullyExpanded={isNavigationFullyExpanded}
            sidebarFooter={sidebarFooter}
            handleNavigationLinkClick={handleNavigationLinkClick}
            handleSetNavigationExpanded={handleSetNavigationExpanded}
            selectedItemIdRef={selectedItemIdRef}
            layoutRef={layoutRef}
            navigation={navigation}
          />
        )}

        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            bgcolor: "inherit",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Toolbar sx={{ displayPrint: "none" }} />
          
          {/* Scrollable main content */}
          <Box
            component="main"
            sx={{
              flex: 1,
              overflowY: "auto"
            }}
          >
            {children}
          </Box>

          {/* Footer that moves with sidebar */}
          {FooterView && (
            <Box
              component="footer"
              sx={{
                flexShrink: 0,
              }}
            >
              <Footer footerTheme={footerTheme}>{FooterView}</Footer>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
});

AppLayout.displayName = "AppLayout";

export default AppLayout;